import React, { useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { toast } from "sonner";
import { fetchMyCircle, removeCircleMember, updateCircleMember } from "../../../store/actions/circleActions";
import AddCircleMemberModal from "../../Circle/AddCircleMemberModal";
import { circleAccessLabel, circleGroup, circleRelationshipText, circleRoleLabel } from "./athleteUtils";
import { initialsOf } from "../../ForBrands/brandDealUtils";
import { Avatar, Badge, Btn, Card, Chip, ConfirmModal, ErrorState, Loading, PageHeader, Select, Toggle } from "../../ForBrands/ui/BrandUI";
import { usePhoneTheme } from "../../ForBrands/useIsPhone";
import CircleMobile from "./mobile/CircleMobile";

// Athlete Mode · My Circle (Figma 03 · My Circle): the people around the
// athlete's career (rep, guardian, coaches, trainers). Brands are not listed
// here; they live in Atlas and My Deals. Below 640px (the phone theme) the
// same data renders as CircleMobile: contact cards instead of the table
// (Figma 2000:831). The modals are shared and open as sheets there.

const FILTERS = [
  { value: "all", label: "Everyone" },
  { value: "team", label: "My team" },
  { value: "representation", label: "Representation" },
];

const PERMISSIONS = [
  { value: "view", label: "View profile" },
  { value: "edit", label: "Edit profile" },
  { value: "manage", label: "Manage profile" },
];

const SUBTITLE = "The people around your career: your rep, your guardian, your coaches and trainers.";

const ACCESS_NOTE =
  "Reps see deal status and timelines, and your financial terms only when you turn them on below. Parents and guardians co-sign contracts when you are under 18. Coaches and trainers see deal status, never dollar values.";

// What the Access cell holds: the financial-terms toggle for a rep, plain
// text for a parent or guardian (they co-sign, the permission does not
// apply), and the permission select for everyone else.
const accessControl = (member) => {
  if (member.relationship === "Rep") return "financials";
  if (member.relationship === "Parent" || member.relationship === "Guardian") return null;
  return "permission";
};

const AthleteCirclePage = () => {
  const dispatch = useDispatch();
  const [people, setPeople] = useState(null);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState("all");
  const [adding, setAdding] = useState(false);
  const [busy, setBusy] = useState(null);
  // The member waiting on the remove confirmation.
  const [removing, setRemoving] = useState(null);
  const phone = usePhoneTheme();

  const load = async () => {
    try {
      const members = await dispatch(fetchMyCircle());
      setPeople(members || []);
      setError(null);
    } catch (err) {
      setError(err.message || "Could not load your Circle");
      setPeople([]);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const rows = useMemo(
    () =>
      (people || []).map((m) => ({
      id: m.id,
      kind: "person",
      name: m.name || m.email,
      subtitle: m.email + (m.hasAccount ? "" : " · no CACHE account yet"),
      initials: initialsOf(...(m.name || m.email || "").split(" ")),
      role: circleRoleLabel(m.relationship),
      relationship: circleRelationshipText(m),
      access: circleAccessLabel(m),
      control: accessControl(m),
      group: circleGroup(m),
      raw: m,
    })),
    [people],
  );

  const counts = useMemo(
    () => ({
      all: rows.length,
      team: rows.filter((r) => r.group === "team").length,
      representation: rows.filter((r) => r.group === "representation").length,
    }),
    [rows],
  );

  const visible = rows.filter((r) => filter === "all" || r.group === filter);

  const changePermission = async (member, permission) => {
    setBusy(member.id);
    try {
      await dispatch(updateCircleMember(member.id, { permission }));
      toast.success(`${member.name || member.email} can now ${PERMISSIONS.find((p) => p.value === permission)?.label.toLowerCase()}.`);
      await load();
    } catch (err) {
      toast.error(err.message || "Could not update access");
    } finally {
      setBusy(null);
    }
  };

  // Reps see deal status and timelines; offer amounts and payouts only when
  // the athlete turns financial terms on for that rep.
  const shareFinancials = async (member, on) => {
    setBusy(member.id);
    try {
      await dispatch(updateCircleMember(member.id, { shareFinancials: on }));
      toast.success(on ? `${member.name || member.email} can now see your financial terms.` : `Financial terms hidden from ${member.name || member.email}.`);
      await load();
    } catch (err) {
      toast.error(err.message || "Could not update access");
    } finally {
      setBusy(null);
    }
  };

  // Runs once the athlete confirms in the ConfirmModal.
  const remove = async (member) => {
    setRemoving(null);
    setBusy(member.id);
    try {
      await dispatch(removeCircleMember(member.id));
      toast.success("Removed from your Circle.");
      await load();
    } catch (err) {
      toast.error(err.message || "Could not remove this member");
    } finally {
      setBusy(null);
    }
  };

  if (people === null) return <Loading />;

  const emptyText = rows.length ? "Nobody in this group yet." : "Your Circle is empty. Add your rep, a parent or guardian, and your coaches.";

  return (
    <div>
      {phone ? (
        <CircleMobile
          subtitle={SUBTITLE}
          accessNote={ACCESS_NOTE}
          error={error}
          filters={FILTERS}
          counts={counts}
          filter={filter}
          onFilter={setFilter}
          rows={visible}
          emptyText={emptyText}
          busy={busy}
          permissions={PERMISSIONS}
          onPermission={changePermission}
          onShareFinancials={shareFinancials}
          onRemove={setRemoving}
          onAdd={() => setAdding(true)}
        />
      ) : (
        <>
          <PageHeader
            eyebrow="Athlete Mode"
            title="My Circle"
            subtitle={SUBTITLE}
            actions={<Btn onClick={() => setAdding(true)}>Add to my Circle</Btn>}
          />

          {error && <ErrorState>{error}</ErrorState>}

          <div className="flex flex-col gap-5 pt-2">
            <Card padding="px-6 py-4" className="flex items-start gap-4">
              <Badge tone="outline" size="m">
                Access
              </Badge>
              <p className="bm-body-m text-bm-muted">{ACCESS_NOTE}</p>
            </Card>

            <div className="flex flex-wrap gap-2">
              {FILTERS.map((f) => (
                <Chip key={f.value} active={filter === f.value} count={counts[f.value]} onClick={() => setFilter(f.value)}>
                  {f.label}
                </Chip>
              ))}
            </div>

            <Card padding="p-0" className="overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full min-w-[900px]">
                  <thead>
                    <tr className="bg-bm-subtle">
                      <th className="bm-th pl-4 text-left">Contact</th>
                      <th className="bm-th text-left">Role</th>
                      <th className="bm-th text-left">Relationship</th>
                      <th className="bm-th text-left">Access</th>
                      <th className="bm-th pr-4 text-right" />
                    </tr>
                  </thead>
                  <tbody>
                    {visible.length === 0 && (
                      <tr>
                        <td colSpan={5} className="px-4 py-10 text-center bm-body-m text-bm-muted">
                          {emptyText}
                        </td>
                      </tr>
                    )}
                    {visible.map((row) => (
                      <tr key={`${row.kind}-${row.id}`} className="border-t border-bm-border">
                        <td className="bm-td pl-4">
                          <span className="flex items-center gap-3">
                            <Avatar initials={row.initials} size={40} tone="outline" />
                            <span className="flex min-w-0 flex-col">
                              <span className="bm-h-xs text-bm-ink">{row.name}</span>
                              <span className="truncate bm-body-s text-bm-muted">{row.subtitle}</span>
                            </span>
                          </span>
                        </td>
                        <td className="bm-td">
                          <Badge tone="outline">{row.role}</Badge>
                        </td>
                        <td className="bm-td text-bm-ink">{row.relationship}</td>
                        <td className="bm-td text-bm-ink">
                          {row.control === "permission" ? (
                            <Select
                              value={row.raw.permission || "view"}
                              onChange={(e) => changePermission(row.raw, e.target.value)}
                              disabled={busy === row.id}
                              className="!h-8 !w-auto !rounded-full !pl-3 !pr-8 bm-label-xs"
                            >
                              {PERMISSIONS.map((p) => (
                                <option key={p.value} value={p.value}>
                                  {p.label}
                                </option>
                              ))}
                            </Select>
                          ) : row.control === "financials" ? (
                            <span className="flex items-center gap-3">
                              <Toggle on={!!row.raw.shareFinancials} onChange={(on) => shareFinancials(row.raw, on)} label="Share financial terms" />
                              <span className="bm-body-s text-bm-muted">{row.access}</span>
                            </span>
                          ) : (
                            row.access
                          )}
                        </td>
                        <td className="bm-td pr-4">
                          <span className="flex items-center justify-end gap-2">
                            <Btn variant="secondary" size="sm" to={`mailto:${row.raw.email}`}>
                              Message
                            </Btn>
                            <button type="button" onClick={() => setRemoving(row.raw)} disabled={busy === row.id} aria-label={`Remove ${row.name}`} className="px-2 bm-label-xs text-bm-muted hover:text-bm-critical">
                              Remove
                            </button>
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          </div>
        </>
      )}

      <AddCircleMemberModal open={adding} onClose={() => setAdding(false)} onAdded={() => load()} />

      {removing && (
        <ConfirmModal
          title="Remove from your Circle"
          body={`Remove ${removing.name || removing.email} from your Circle? You can add them again later.`}
          ackLabel="I understand they lose their access right away."
          confirmLabel="Remove"
          tone="danger"
          onConfirm={() => remove(removing)}
          onClose={() => setRemoving(null)}
        />
      )}
    </div>
  );
};

export default AthleteCirclePage;
